import { Application, AppStatuses } from '../../gql/graphql';
import { APP_FINISHED_LEASE_STATUSES, PAST_APPROVED_STATUSES } from '../../constants/app-status';
import { koalafiRoles, merchantRoles, Role } from '../../types/role';
import { ValidatedByTokenType } from '../../state-machine/types/ecommMachine.types';

interface ContractSummaryContext {
  contractSummaryPageParam: boolean;
  merchantContractSummaryPageParam: boolean;
  highestSupportedApplicationMilestone: number;
  role: Role;
  application?: Application;
  validatedByTokenType?: ValidatedByTokenType;
}

function m4AndAppStatusPastApproved(ctx: ContractSummaryContext): boolean {
  const isMilestone4 = ctx.highestSupportedApplicationMilestone > 3;
  const appStatusPastApproved = PAST_APPROVED_STATUSES.includes(ctx.application?.lease?.status as AppStatuses);
  return isMilestone4 && appStatusPastApproved;
}

export function canAccessContractSummaryPage(ctx: ContractSummaryContext): boolean {
  if (!ctx.contractSummaryPageParam) return false;
  const hasElevatedRole = [merchantRoles, koalafiRoles].some((arr) => arr.includes(ctx.role));
  return m4AndAppStatusPastApproved(ctx) && (hasElevatedRole || ctx.validatedByTokenType === ValidatedByTokenType.CONTRACT);
}

export function canAccessMerchantContractSummaryPage(ctx: ContractSummaryContext): boolean {
  if (!ctx.merchantContractSummaryPageParam) return false;
  const hasElevatedRole = [merchantRoles, koalafiRoles].some((arr) => arr.includes(ctx.role));
  return m4AndAppStatusPastApproved(ctx) && hasElevatedRole;
}

// OR-2844: viewing an already signed lease is read-only, so unlike the contract summary it is not
// gated on the dealer's milestone. That milestone is the store's current one, not the one the lease
// was signed under, so gating on it sent every lease from a pre-M4 store to the "all done" page
// even though its signed documents are in document persistence.
export function canViewSignedLeaseAgreement(ctx: ContractSummaryContext): boolean {
  const appStatusFinished = APP_FINISHED_LEASE_STATUSES.includes(ctx.application?.lease?.status as AppStatuses);
  if (!appStatusFinished) return false;
  const hasElevatedRole = [merchantRoles, koalafiRoles].some((arr) => arr.includes(ctx.role));
  if (ctx.contractSummaryPageParam && (hasElevatedRole || ctx.validatedByTokenType === ValidatedByTokenType.CONTRACT)) return true;
  return ctx.merchantContractSummaryPageParam && hasElevatedRole;
}

export function canAccessLeaseAgreement(role: Role, milestone: number, appStatus?: AppStatuses, token?: ValidatedByTokenType): boolean {
  const hasElevatedRole = [merchantRoles, koalafiRoles].some((arr) => arr.includes(role));
  const isMilestone4 = milestone > 3;
  const appStatusPastApproved = appStatus ? PAST_APPROVED_STATUSES.includes(appStatus) : false;
  // a signed lease stays viewable whatever the store's milestone (OR-2844)
  const appStatusFinished = appStatus ? APP_FINISHED_LEASE_STATUSES.includes(appStatus) : false;
  const hasContractSummaryParam = new URLSearchParams(document.location.search).has('contractSummaryPage');

  if (((isMilestone4 && appStatusPastApproved) || appStatusFinished) && hasContractSummaryParam) {
    if (hasElevatedRole || token === ValidatedByTokenType.CONTRACT) {
      return true;
    }
  }
  return false;
}

export function canAccessApplicationDetails(role: Role): boolean {
  const hasElevatedRole = [merchantRoles, koalafiRoles].some((arr) => arr.includes(role));
  const hasReviewPageParam = new URLSearchParams(document.location.search).get('reviewPage') === 'true';
  return hasElevatedRole && hasReviewPageParam;
}
